export const resizeImage = (
  source: HTMLImageElement | HTMLCanvasElement | HTMLVideoElement,
  targetWidth: number = 640,
  targetHeight: number = 480
): HTMLCanvasElement => {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  if (!ctx) {
    throw new Error("Could not get canvas context");
  }

  canvas.width = targetWidth;
  canvas.height = targetHeight;

  // Check if source is a video element
  const isVideo = source instanceof HTMLVideoElement;
  const sourceWidth = isVideo ? source.videoWidth : source.width;
  const sourceHeight = isVideo ? source.videoHeight : source.height;

  const sourceAspect = sourceWidth / sourceHeight;
  const targetAspect = targetWidth / targetHeight;

  let drawWidth = targetWidth;
  let drawHeight = targetHeight;
  let offsetX = 0;
  let offsetY = 0;

  if (sourceAspect > targetAspect) {
    drawHeight = targetHeight;
    drawWidth = drawHeight * sourceAspect;
    offsetX = (targetWidth - drawWidth) / 2;
  } else {
    drawWidth = targetWidth;
    drawHeight = drawWidth / sourceAspect;
    offsetY = (targetHeight - drawHeight) / 2;
  }

  ctx.fillStyle = "#FFFFFF";
  ctx.fillRect(0, 0, targetWidth, targetHeight);

  ctx.drawImage(source, offsetX, offsetY, drawWidth, drawHeight);

  return canvas;
};

export const convertToBase64PNG = async (
  imageSource: HTMLCanvasElement | HTMLImageElement | HTMLVideoElement,
  maxSizeKB: number = 1000
): Promise<string> => {
  let canvas = resizeImage(imageSource, 640, 480);
  const maxSizeBytes = maxSizeKB * 1024;

  let dataUrl = canvas.toDataURL("image/png");
  let base64String = dataUrl.split(",")[1];

  let sizeBytes = (base64String.length * 3) / 4;

  let scale = 0.9;
  while (sizeBytes > maxSizeBytes && scale > 0.5) {
    const newWidth = Math.floor(640 * scale);
    const newHeight = Math.floor(480 * scale);
    canvas = resizeImage(imageSource, newWidth, newHeight);
    dataUrl = canvas.toDataURL("image/png");
    base64String = dataUrl.split(",")[1];
    sizeBytes = (base64String.length * 3) / 4;
    scale -= 0.1;
  }

  return base64String;
};

export const prepareImageForVerification = async (
  source: HTMLVideoElement | HTMLImageElement | HTMLCanvasElement
): Promise<string> => {
  try {
    const base64String = await convertToBase64PNG(source, 1000);

    const sizeKB = (base64String.length * 3) / 4 / 1024;
    if (sizeKB > 1000) {
      console.warn(`Image size (${sizeKB.toFixed(2)}KB) exceeds 1MB limit`);
    }

    if (!base64String || base64String.length === 0) {
      throw new Error("Failed to generate base64 image");
    }

    return base64String;
  } catch (error: any) {
    console.error("Error preparing image:", error);
    throw new Error(
      "Failed to prepare image for verification: " + error.message
    );
  }
};
